import { useState, useEffect, useRef } from 'react'
import { Spinner } from 'amazon-chime-sdk-component-library-react'
import { AISummaryStatus, AISummaryType, useGetAISummary } from 'hooks/useGetAISummary'
import useHasFeature, { AppFeatures, AppSubFeatures } from 'hooks/useHasFeature'
import AIGradient from 'images/ai-gradient.svg'
import AIPurple from 'images/ai-purple.svg'
import AIWhite from 'images/ai-white.svg'
import { ContactState } from 'store/case/case.state'
import styles from './AISummary.module.scss'

interface Props {
    contact: ContactState
    compact?: boolean
    acw?: boolean
    title?: string
    type?: AISummaryType
}

const AISummary: React.FC<Props> = ({
    compact,
    title = 'AI Summary',
    acw,
    type,
    contact,
    ...props
}) => {
    const [collapsed, setCollapsed] = useState(false)
    const contentRef = useRef<HTMLDivElement>(null)
    const previousStatusRef = useRef<AISummaryStatus | null>(null)

    const hasFeature = useHasFeature()

    const hasContactLensVoice = hasFeature(AppFeatures.AI_FEATURES, [
        AppSubFeatures.VOICE_SUMMARIES,
    ])

    const hasContactLensChat = hasFeature(AppFeatures.AI_FEATURES, [AppSubFeatures.CHAT_SUMMARIES])

    const hasPreContactSummaries = hasFeature(AppFeatures.AI_FEATURES, [
        AppSubFeatures.AI_ACTION_FLOWS_PRE_CONTACT_SUMMARIES_EMAIL,
        AppSubFeatures.AI_ACTION_FLOWS_PRE_CONTACT_SUMMARIES_CHAT,
    ])

    const hasPermission = hasContactLensVoice || hasContactLensChat || hasPreContactSummaries

    const { status, summaryText } = useGetAISummary({
        contact,
        type: type ?? (acw ? AISummaryType.REALTIME_CHAT : AISummaryType.VOICE_CHAT),
    })

    const loading = status === AISummaryStatus.PENDING
    const error = status === AISummaryStatus.ERROR

    // Focus content when it loads for screen reader announcement
    useEffect(() => {
        if (previousStatusRef.current === AISummaryStatus.PENDING && 
            status === AISummaryStatus.SUCCESS && 
            contentRef.current) {
            contentRef.current.focus()
        }
        previousStatusRef.current = status
    }, [status])

    const handleToggleCollapse = () => {
        setCollapsed(!collapsed)
    }

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            handleToggleCollapse()
        }
    }

    const renderContent = () => {
        if (loading) {
            return (
                <div className={styles['sa-ai-summary-no-data']}>
                    <Spinner className={styles['sa-ai-summary-spinner']} />
                    <p>{summaryText}</p>
                </div>
            )
        }
        
        if (error) {
            return (
                <div className={styles['sa-ai-summary-no-data']}>
                    <p className={styles['message']}>{summaryText}</p>
                </div>
            )
        }
        
        return <span aria-live="polite" className={styles['message']}>{summaryText}</span>
    }

    if (!hasPermission || status === AISummaryStatus.TOO_EARLY) {
        return null
    }

    if (compact) {
        return (
            <div className={styles['sa-ai-summary-compact']}>
                <div className={styles['gradient-logo-background']}>
                    <img src={AIWhite} title="AI" alt="AI" />
                </div>
                {loading ? (
                    <div className={styles['sa-ai-summary-row']}>
                        <Spinner className={styles['sa-ai-summary-spinner']} />
                        <p>{summaryText}</p>
                    </div>
                ) : (
                    <span className={styles['compact-message']}>{summaryText}</span>
                )}
            </div>
        )
    }

    const ChevronUp = (
        <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={styles['collapse-icon']}
        >
            <path
                d="M18 15L12 9L6 15"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    )

    const ChevronDown = (
        <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={styles['collapse-icon']}
        >
            <path
                d="M6 9L12 15L18 9"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    )

    return (
        <section 
            className={styles['sa-ai-summary']}
            {...props}
            data-testid="sa-ai-summary"
            data-title={title}
            aria-labelledby="ai-summary-heading"
            aria-live="polite"
            ref={contentRef}
        >
            <div className={styles['inner-container']}>
                <div className={styles['header']}>
                    <div className={styles['header-left']}>
                        <img src={AIGradient} title="AI" alt="AI" />
                        <h3 id="ai-summary-heading" aria-live="polite" className={styles['heading']}>{title}</h3>
                        <img
                            src={AIPurple}
                            title="AI"
                            alt="AI"
                            className={styles['icon-margin']}
                        />
                        <span aria-live="polite" className={styles['summary-source']}>Generated by AI</span>
                    </div>
                    <button
                        className={styles['collapse-button']}
                        onClick={handleToggleCollapse}
                        onKeyDown={handleKeyDown}
                        aria-expanded={!collapsed}
                        aria-controls="ai-summary-content"
                        aria-label={`Toggle ${title} ${collapsed ? 'collapsed. Click to open' : 'open. Click to close'}`}
                        type="button"
                    >
                        {collapsed ? ChevronDown : ChevronUp}
                    </button>
                </div>

                <section 
                    id="ai-summary-content"
                    className={styles['content']}
                    aria-hidden={collapsed}
                    aria-live="polite"
                    aria-labelledby="ai-summary-heading"
                    style={{ display: collapsed ? 'none' : 'block' }}
                >
                    {renderContent()}
                </section>
            </div>
        </section>
    )
}

export default AISummary
